import {
  computeCropRect,
  type CropOrigin,
} from "./reframe-crop-geometry";
import {
  DETECTION_SOURCE_LABELS,
  dominantDetectionSource,
  selectPrimarySubject,
  type SubjectDetectionResult,
  type SubjectDetectionSource,
} from "./reframe-subject-detection";
import type { ImageDataLike } from "./reframe-skin-tone-detector";
import {
  ReframeSubjectDetector,
  type SubjectDetectorOptions,
} from "./reframe-subject-detector";
import {
  ASPECT_RATIO_PRESETS,
  type AspectRatioConfig,
  type AspectRatioPreset,
} from "../layout/aspect-ratios";

export type PlatformPreset =
  | "youtube"
  | "tiktok"
  | "instagram-reels"
  | "instagram-feed"
  | "instagram-stories"
  | "youtube-shorts"
  | "facebook"
  | "twitter"
  | "linkedin";

/**
 * A publishing destination, resolved to the canvas it wants. The dimensions
 * come from the canonical aspect ratio list so a platform and the ratio it maps
 * to can never drift apart.
 */
export interface PlatformPresetConfig {
  name: string;
  ratio: number;
  width: number;
  height: number;
  platform: PlatformPreset;
}

const forPlatform = (
  platform: PlatformPreset,
  name: string,
  ratio: AspectRatioPreset,
): PlatformPresetConfig => {
  const config = ASPECT_RATIO_PRESETS[ratio];
  return {
    name,
    ratio: config.ratio,
    width: config.width,
    height: config.height,
    platform,
  };
};

export const PLATFORM_PRESETS: Record<PlatformPreset, PlatformPresetConfig> = {
  youtube: forPlatform("youtube", "YouTube", "16:9"),
  tiktok: forPlatform("tiktok", "TikTok", "9:16"),
  "instagram-reels": forPlatform("instagram-reels", "Instagram Reels", "9:16"),
  "instagram-feed": forPlatform("instagram-feed", "Instagram Feed", "1:1"),
  "instagram-stories": forPlatform(
    "instagram-stories",
    "Instagram Stories",
    "9:16",
  ),
  "youtube-shorts": forPlatform("youtube-shorts", "YouTube Shorts", "9:16"),
  facebook: forPlatform("facebook", "Facebook", "16:9"),
  twitter: forPlatform("twitter", "Twitter/X", "16:9"),
  linkedin: forPlatform("linkedin", "LinkedIn", "16:9"),
};

export interface DetectedFace {
  x: number;
  y: number;
  width: number;
  height: number;
  confidence: number;
}

export interface ReframeKeyframe {
  time: number;
  cropX: number;
  cropY: number;
  cropWidth: number;
  cropHeight: number;
  scale: number;
}

export interface ReframeSettings {
  targetAspectRatio: AspectRatioPreset;
  customRatio?: number;
  trackingSpeed: number;
  padding: number;
  smoothing: number;
  followSubject: boolean;
  centerBias: number;
  /** Abandon a run in progress, e.g. the panel closed or the clip changed. */
  signal?: AbortSignal;
}

/**
 * How often the analysis loop hands the main thread back.
 *
 * Detection is synchronous inference, so nothing repaints between frames
 * without an explicit macrotask. Every eighth frame keeps the progress bar
 * moving without paying the timer cost on every single one.
 */
const ANALYZE_YIELD_EVERY = 8;

export const DEFAULT_REFRAME_SETTINGS: ReframeSettings = {
  targetAspectRatio: "9:16",
  trackingSpeed: 0.5,
  padding: 0.1,
  smoothing: 0.8,
  followSubject: true,
  centerBias: 0.3,
};

export interface ReframeResult {
  keyframes: ReframeKeyframe[];
  outputWidth: number;
  outputHeight: number;
  success: boolean;
  message?: string;
  /**
   * The signal that carried most frames. Callers have to distinguish a tracked
   * run from a centred crop that found nothing, and from the skin-tone
   * fallback, in order to report the run honestly. Recovering that from
   * `message` would mean parsing prose, so it is reported as data.
   */
  detectionSource?: SubjectDetectionSource;
}

type ProgressCallback = (progress: number, message: string) => void;

export class AutoReframeEngine {
  private canvas: OffscreenCanvas | null = null;
  private ctx: OffscreenCanvasRenderingContext2D | null = null;
  private initialized = false;
  private detectionCache: Map<number, SubjectDetectionResult> = new Map();
  private detector: ReframeSubjectDetector;

  constructor(options: SubjectDetectorOptions = {}) {
    this.detector = new ReframeSubjectDetector(options);
  }

  async initialize(onProgress?: ProgressCallback): Promise<void> {
    if (this.initialized) return;

    onProgress?.(10, "Initializing auto-reframe engine...");

    this.canvas = new OffscreenCanvas(1920, 1080);
    this.ctx = this.canvas.getContext("2d");

    onProgress?.(40, "Loading subject detection models...");
    const modelsReady = await this.detector.prepare();

    onProgress?.(
      100,
      modelsReady
        ? "Auto-reframe engine ready"
        : "Auto-reframe ready (detection models unavailable)",
    );
    this.initialized = true;
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  /** False once initialized means detection fell back to the skin-tone pass. */
  hasModelBackedDetection(): boolean {
    return this.detector.isModelBacked();
  }

  async analyzeClip(
    frames: ImageBitmap[],
    frameRate: number,
    settings: ReframeSettings,
    onProgress?: ProgressCallback,
  ): Promise<ReframeResult> {
    if (!this.initialized || !this.ctx) {
      return {
        keyframes: [],
        outputWidth: 1920,
        outputHeight: 1080,
        success: false,
        message: "Engine not initialized",
        detectionSource: "none",
      };
    }

    const targetConfig = this.getTargetConfig(settings);
    const keyframes: ReframeKeyframe[] = [];
    const sourceWidth = frames[0]?.width || 1920;
    const sourceHeight = frames[0]?.height || 1080;

    onProgress?.(0, "Analyzing video frames...");

    let previousCrop: CropOrigin | null = null;
    const sourceCounts = new Map<SubjectDetectionSource, number>();

    for (let i = 0; i < frames.length; i++) {
      const frame = frames[i];
      const time = i / frameRate;
      const progress = Math.round((i / frames.length) * 100);

      onProgress?.(progress, `Analyzing frame ${i + 1}/${frames.length}`);

      // Detection is synchronous WASM inference, and awaiting an
      // already-settled promise only yields a microtask, so without this the
      // whole loop runs inside one task: the browser never paints, the
      // progress reported just above never appears, and the editor looks
      // hung for the length of the analysis. A macrotask every few frames
      // costs little next to the inference itself.
      if (i > 0 && i % ANALYZE_YIELD_EVERY === 0) {
        await new Promise((resolve) => setTimeout(resolve, 0));
      }

      if (settings.signal?.aborted) {
        return {
          keyframes: [],
          outputWidth: targetConfig.width,
          outputHeight: targetConfig.height,
          success: false,
          message: "Analysis cancelled",
        };
      }

      const detection = await this.detectSubjects(frame, i, time * 1000);
      sourceCounts.set(
        detection.source,
        (sourceCounts.get(detection.source) ?? 0) + 1,
      );

      const crop = computeCropRect({
        sourceWidth,
        sourceHeight,
        targetRatio: targetConfig.ratio,
        subject: selectPrimarySubject(
          detection.subjects,
          sourceWidth,
          sourceHeight,
        ),
        options: settings,
        previousCrop,
      });

      previousCrop = { x: crop.x, y: crop.y };

      keyframes.push({
        time,
        cropX: crop.x,
        cropY: crop.y,
        cropWidth: crop.width,
        cropHeight: crop.height,
        scale: 1,
      });
    }

    const smoothedKeyframes = this.smoothKeyframes(
      keyframes,
      settings.smoothing,
    );

    onProgress?.(100, "Analysis complete");

    const detectionSource = dominantDetectionSource(sourceCounts);

    return {
      keyframes: smoothedKeyframes,
      outputWidth: targetConfig.width,
      outputHeight: targetConfig.height,
      success: true,
      message: `Tracked with ${DETECTION_SOURCE_LABELS[detectionSource]}`,
      detectionSource,
    };
  }

  async reframeFrame(
    frame: ImageBitmap,
    keyframe: ReframeKeyframe,
    outputWidth: number,
    outputHeight: number,
  ): Promise<ImageBitmap> {
    if (!this.ctx || !this.canvas) {
      return frame;
    }

    this.canvas.width = outputWidth;
    this.canvas.height = outputHeight;

    this.ctx.drawImage(
      frame,
      keyframe.cropX,
      keyframe.cropY,
      keyframe.cropWidth,
      keyframe.cropHeight,
      0,
      0,
      outputWidth,
      outputHeight,
    );

    return createImageBitmap(this.canvas);
  }

  private getTargetConfig(settings: ReframeSettings): AspectRatioConfig {
    if (settings.targetAspectRatio === "custom" && settings.customRatio) {
      const ratio = settings.customRatio;
      if (ratio >= 1) {
        return {
          id: "custom",
          name: "Custom",
          ratio,
          width: 1920,
          height: Math.round(1920 / ratio),
        };
      } else {
        return {
          id: "custom",
          name: "Custom",
          ratio,
          width: Math.round(1080 * ratio),
          height: 1080,
        };
      }
    }
    return ASPECT_RATIO_PRESETS[settings.targetAspectRatio];
  }

  private async detectSubjects(
    frame: ImageBitmap,
    frameIndex: number,
    timestampMs: number,
  ): Promise<SubjectDetectionResult> {
    const cached = this.detectionCache.get(frameIndex);
    if (cached) return cached;

    const detection = await this.detector.detect({
      frame,
      timestampMs,
      readImageData: () => this.readFrameImageData(frame),
    });
    this.detectionCache.set(frameIndex, detection);
    return detection;
  }

  private readFrameImageData(frame: ImageBitmap): ImageDataLike | null {
    if (!this.ctx || !this.canvas) return null;

    this.canvas.width = frame.width;
    this.canvas.height = frame.height;
    this.ctx.drawImage(frame, 0, 0);

    return this.ctx.getImageData(0, 0, frame.width, frame.height);
  }

  private smoothKeyframes(
    keyframes: ReframeKeyframe[],
    smoothing: number,
  ): ReframeKeyframe[] {
    if (keyframes.length < 3 || smoothing === 0) return keyframes;

    const smoothed: ReframeKeyframe[] = [];
    const windowSize = Math.max(3, Math.round(smoothing * 10));

    for (let i = 0; i < keyframes.length; i++) {
      const start = Math.max(0, i - Math.floor(windowSize / 2));
      const end = Math.min(keyframes.length, i + Math.ceil(windowSize / 2));

      let sumX = 0,
        sumY = 0;
      let count = 0;

      for (let j = start; j < end; j++) {
        sumX += keyframes[j].cropX;
        sumY += keyframes[j].cropY;
        count++;
      }

      smoothed.push({
        ...keyframes[i],
        cropX: Math.round(sumX / count),
        cropY: Math.round(sumY / count),
      });
    }

    return smoothed;
  }

  getKeyframeAtTime(
    keyframes: ReframeKeyframe[],
    time: number,
  ): ReframeKeyframe {
    if (keyframes.length === 0) {
      return {
        time: 0,
        cropX: 0,
        cropY: 0,
        cropWidth: 1920,
        cropHeight: 1080,
        scale: 1,
      };
    }

    if (time <= keyframes[0].time) return keyframes[0];
    if (time >= keyframes[keyframes.length - 1].time)
      return keyframes[keyframes.length - 1];

    for (let i = 0; i < keyframes.length - 1; i++) {
      if (time >= keyframes[i].time && time < keyframes[i + 1].time) {
        const t =
          (time - keyframes[i].time) /
          (keyframes[i + 1].time - keyframes[i].time);
        return this.interpolateKeyframes(keyframes[i], keyframes[i + 1], t);
      }
    }

    return keyframes[keyframes.length - 1];
  }

  private interpolateKeyframes(
    a: ReframeKeyframe,
    b: ReframeKeyframe,
    t: number,
  ): ReframeKeyframe {
    const ease = t * t * (3 - 2 * t);
    return {
      time: a.time + (b.time - a.time) * t,
      cropX: Math.round(a.cropX + (b.cropX - a.cropX) * ease),
      cropY: Math.round(a.cropY + (b.cropY - a.cropY) * ease),
      cropWidth: Math.round(a.cropWidth + (b.cropWidth - a.cropWidth) * ease),
      cropHeight: Math.round(
        a.cropHeight + (b.cropHeight - a.cropHeight) * ease,
      ),
      scale: a.scale + (b.scale - a.scale) * ease,
    };
  }

  clearCache(): void {
    this.detectionCache.clear();
  }

  dispose(): void {
    this.canvas = null;
    this.ctx = null;
    this.detectionCache.clear();
    this.detector.close();
    this.initialized = false;
  }
}

let autoReframeEngineInstance: AutoReframeEngine | null = null;

export function getAutoReframeEngine(): AutoReframeEngine | null {
  return autoReframeEngineInstance;
}

export function initializeAutoReframeEngine(
  options: SubjectDetectorOptions = {},
): AutoReframeEngine {
  if (!autoReframeEngineInstance) {
    autoReframeEngineInstance = new AutoReframeEngine(options);
  }
  return autoReframeEngineInstance;
}

export function disposeAutoReframeEngine(): void {
  if (autoReframeEngineInstance) {
    autoReframeEngineInstance.dispose();
    autoReframeEngineInstance = null;
  }
}
